import {PackageType} from '@sollapay/enums';

import {ComplianceStep, getStepKeysForTab, TrustDetailsStep} from '../config';
import {TrustScopeFormObjectSchema} from '../validation';

import type {TrustTypeStrategy} from '@sollapay/domain/trust-types';
import type {PackageMissingItem} from '@sollapay/types';

const TRUST_SCOPE_FIELDS = new Set(Object.keys(TrustScopeFormObjectSchema.shape));

// Every trust-details document is an asset document.
function stepForTrustDetailsItem(item: PackageMissingItem): TrustDetailsStep {
  if (item.code === 'MISSING_ASSET' || item.documentType) return TrustDetailsStep.ASSET_DETAILS;
  if (item.field && TRUST_SCOPE_FIELDS.has(item.field)) return TrustDetailsStep.TRUST_SCOPE;
  return TrustDetailsStep.SELLER_DETAILS;
}

function stepForComplianceItem(item: PackageMissingItem): ComplianceStep {
  if (item.code === 'MISSING_FATCA_CRS') return ComplianceStep.FATCA_CRS;
  if (item.code === 'MISSING_FATCA_CRS_SUPPLEMENTARY') return ComplianceStep.SUPPLEMENTARY;
  return ComplianceStep.SUPPORTING_DOCUMENTS;
}

function resolveStepForItem(type: PackageType, item: PackageMissingItem): string | undefined {
  if (type === PackageType.TRUST_DETAILS) return stepForTrustDetailsItem(item);
  if (type === PackageType.COMPLIANCE) return stepForComplianceItem(item);
  return undefined;
}

/** Steps that still have at least one missing item. */
export function getIncompleteSteps(type: PackageType, missing: PackageMissingItem[]): Set<string> {
  return new Set(
    missing
      .map(item => resolveStepForItem(type, item))
      .filter((step): step is string => step !== undefined)
  );
}

/**
 * First step (in tab order) containing at least one missing item — the step the user
 * should land on to see and complete what's blocking readiness.
 */
export function resolveFirstIncompleteStep(
  type: PackageType,
  missing: PackageMissingItem[],
  strategy: TrustTypeStrategy
): string | undefined {
  if (missing.length === 0) return undefined;

  const incompleteSteps = getIncompleteSteps(type, missing);
  return getStepKeysForTab(type, strategy).find(step => incompleteSteps.has(step));
}
